/**
 * AIDisclaimer — Mention déontologique standardisée pour toute zone où des
 * contenus sont générés ou structurés par IA dans CONFILEX.
 *
 * Conformément aux recommandations des instances représentatives des
 * professions juridiques (avocats, notaires, experts-comptables, commissaires
 * de justice), CONFILEX ne délivre AUCUN avis juridique, fiscal ou financier.
 * L'IA est uniquement un outil d'organisation et de préparation documentaire.
 *
 * Variants :
 *   - `inline` (défaut) : encart léger sous un contenu IA
 *   - `card` : encart visible et structuré (workspace, audit)
 *   - `footer` : bandeau permanent en pied de zone
 *   - `compact` : 1 ligne uniquement (modal, popup)
 *
 * Le composant est SOBRE et institutionnel — pas d'alarmisme, ton
 * cabinet d'affaires.
 */
import { Info, ShieldCheck, UserCheck } from "lucide-react";

const STATEMENTS = {
  documentaire: "Les contenus générés automatiquement ont une vocation informative et documentaire.",
  verify: "Les informations produites par CONFILEX doivent être vérifiées par un professionnel compétent avant toute décision.",
  no_advice: "CONFILEX facilite la préparation et l'organisation des dossiers mais ne délivre aucun avis juridique, fiscal ou financier.",
  expert_master: "Le spécialiste conserve l'entière maîtrise de son analyse, de ses conclusions et de ses recommandations.",
};

export default function AIDisclaimer({ variant = "inline", statements = null, className = "" }) {
  // Statements par défaut selon la variante
  const items = statements || (
    variant === "compact" ? ["verify"] :
    variant === "footer" ? ["documentaire", "no_advice"] :
    ["documentaire", "verify", "no_advice"]
  );

  if (variant === "compact") {
    return (
      <p className={`text-[11px] text-slate-500 italic leading-relaxed flex items-start gap-1.5 ${className}`}
        data-testid="ai-disclaimer-compact">
        <Info className="w-3 h-3 mt-0.5 shrink-0 text-[#C9A961]" />
        <span>{STATEMENTS[items[0]]}</span>
      </p>
    );
  }

  if (variant === "card") {
    return (
      <div className={`bg-[#F8F6F1] border border-[#C9A961]/25 rounded-xl p-4 ${className}`}
        data-testid="ai-disclaimer-card">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-[#C9A961]" />
          <p className="text-xs uppercase tracking-wider text-[#C9A961] font-medium">
            Mention déontologique
          </p>
        </div>
        <ul className="space-y-1.5">
          {items.map((k) => (
            <li key={k} className="text-xs text-slate-700 leading-relaxed flex gap-2">
              <span className="text-[#C9A961] shrink-0">•</span>
              <span>{STATEMENTS[k]}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (variant === "footer") {
    return (
      <div className={`border-t border-slate-200/60 pt-4 mt-6 flex items-start gap-2 ${className}`}
        data-testid="ai-disclaimer-footer">
        <UserCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#C9A961]" />
        <div>
          {items.map((k) => (
            <p key={k} className="text-[11px] text-slate-500 leading-relaxed">{STATEMENTS[k]}</p>
          ))}
        </div>
      </div>
    );
  }

  // inline (défaut)
  return (
    <div className={`bg-slate-50/60 border-l-2 border-[#C9A961] pl-3 py-2 ${className}`}
      data-testid="ai-disclaimer-inline">
      {items.map((k) => (
        <p key={k} className="text-[11px] text-slate-600 leading-relaxed italic">
          {STATEMENTS[k]}
        </p>
      ))}
    </div>
  );
}
